import { useEffect, useSyncExternalStore } from "react";
// `next/compat/router` — 라우터가 없을 때 예외 대신 null. 이 훅은 로고(BHapticsLogo)를 통해 푸터·결제 헤더처럼
// 라우터 없이 렌더되는 자리(단위 테스트 등)에서도 불린다 — `next/router` 는 거기서 「NextRouter was not mounted」로 죽는다.
import { useRouter } from "next/compat/router";
import { isProd } from "@/Env";
import {
  HALLOWEEN_2026_PREVIEW_PARAM,
  HALLOWEEN_2026_PREVIEW_OFF_STORAGE_KEY,
  HALLOWEEN_2026_WINDOW_GLOBAL,
  isHalloween2026Active,
  isWithinHalloween2026,
} from "@/constants/halloween-2026";

/**
 * 비-prod 의 「이 탭은 끔」을 기억한다 — 쿼리는 사이트 안에서 이동하면 사라지므로, `=0` 으로 끈 뒤 다른
 * 페이지로 가도 꺼진 채로 둔다. `=1` 이면 다시 켠다.
 *
 * 서버는 sessionStorage 를 못 보므로 서버 스냅샷은 `false`(안 끔) — 끈 탭에서 쿼리 없이 새로 열면 첫 렌더는
 * 켜진 채 그렸다가 hydration 직후 꺼진다(비-prod 한정, prod 는 이 값을 보지 않는다).
 */
const listeners = new Set<() => void>();
const readStoredOff = () => {
  try {
    return (
      sessionStorage.getItem(HALLOWEEN_2026_PREVIEW_OFF_STORAGE_KEY) === "1"
    );
  } catch {
    return false;
  }
};
const writeStoredOff = (off: boolean) => {
  try {
    if (off)
      sessionStorage.setItem(HALLOWEEN_2026_PREVIEW_OFF_STORAGE_KEY, "1");
    else sessionStorage.removeItem(HALLOWEEN_2026_PREVIEW_OFF_STORAGE_KEY);
  } catch {
    // 저장소를 못 쓰는 환경(사파리 프라이빗 등) — `=0` 이 붙은 페이지에서만 꺼진다
  }
  listeners.forEach((fn) => fn());
};
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

/** `_document` 가 HTML 을 만들 때 세운 판정 — 그 HTML 의 마크업과 같은 값이다. */
const bakedWithinWindow = () =>
  (window as unknown as Record<string, unknown>)[
    HALLOWEEN_2026_WINDOW_GLOBAL
  ] === true;

/**
 * 이 페이지의 HTML 이 **요청 때** 만들어졌나 — `__NEXT_DATA__` 의 데이터 함수 표식으로 가른다.
 * 정적 최적화(`autoExport`) · `getStaticProps`(`gsp`, ISR 포함) 페이지는 빌드(또는 재검증) 때 만든 HTML 이라 false.
 */
const isRequestTimeHtml = () => {
  const d = (
    window as unknown as {
      __NEXT_DATA__?: { gssp?: boolean; gip?: boolean; appGip?: boolean };
    }
  ).__NEXT_DATA__;
  return d?.gssp === true || d?.gip === true || d?.appGip === true;
};

const subscribeNothing = () => () => {};

/**
 * 할로윈 2026 장식을 켤까 (#6061) — 로고 · 커서 · 홈 오버레이 · 팝업 · 띠배너가 모두 이 훅을 본다.
 *
 * 기간 판정의 **시간 소스는 서버**다: 서버 렌더는 서버 시계로, 클라는 `_document` 가 서버 시계로 세운
 * 전역(`HALLOWEEN_2026_WINDOW_GLOBAL`)을 읽는다 — 띠배너(`PromoBanner`)와 같은 구조. 그래서 기기 시계가
 * 틀려도 SSR 과 hydration 이 같은 결론을 낸다.
 *
 * ## ⚠️ 요청이 없는 페이지(정적 · SSG)는 hydration 직후 기기 시계로 다시 판정한다 (#6066 R1)
 *
 * 그 HTML 은 **빌드 때** 만들어져 `_document` 의 판정도 빌드 시각으로 굳는다 — 기간 중에 배포하고 그 뒤 재배포가
 * 없으면 이벤트가 끝나도 404 · 블로그 목록 · 약관에 호박 로고가 남는다(`_document` docblock 「서버 시각은 요청 렌더
 * 페이지에서만」). 띠배너는 그런 페이지에서 **아예 안 그리는** 쪽을 택했지만(`hidePromoBanner`), 로고는 모든 페이지에
 * 같게 보여야 해서 끄지 않는다. 대신:
 *
 * ```
 *   hydration 중   구워진 값(전역)  — HTML 마크업과 같아 mismatch 가 없다
 *   hydration 뒤   요청 렌더 페이지 → 구워진 값 그대로 · 정적/SSG 페이지 → 기기 시계
 * ```
 *
 * 기기 시계가 틀린 사용자는 정적 페이지에서만 경계 시각이 어긋난다 — 배포 시각에 따라 일주일씩 굳는 것보다 작다.
 *
 * ⚠️ 전역은 `=== true` 로 좁게 읽는다. 스크립트가 안 돌았으면 「꺼짐」으로 떨어진다.
 * ⚠️ 비-prod 는 기간 안이면 기본 켬 — 미리보기 쿼리(`=0` 끔 · `=1` 기간 밖에서도 켬)는 `router.query` 라 SSR 과 클라가 같은 값을 본다.
 */
export function useHalloween2026(): boolean {
  const query = useRouter()?.query ?? {};
  const param = query[HALLOWEEN_2026_PREVIEW_PARAM];
  const storedOff = useSyncExternalStore(subscribe, readStoredOff, () => false);
  const withinWindow = useSyncExternalStore(
    subscribeNothing,
    () =>
      isRequestTimeHtml()
        ? bakedWithinWindow()
        : isWithinHalloween2026(new Date()),
    () =>
      typeof window === "undefined"
        ? isWithinHalloween2026(new Date())
        : bakedWithinWindow(),
  );

  useEffect(() => {
    if (isProd) return;
    if (param === "0" && !readStoredOff()) writeStoredOff(true);
    if (param === "1" && readStoredOff()) writeStoredOff(false);
  }, [param]);

  const forced = param === "1";
  const preview = forced || (param !== "0" && !storedOff);
  return isHalloween2026Active({ isProd, withinWindow, preview, forced });
}
